import Box from '@mui/material/Box';
import {
  FilterDateButton,
  type FilterDatePreset,
  type FilterDateValue,
} from '@openx/design-system/components/Filter/FilterDateButton.tsx';
import { FilterDropdownButton } from '@openx/design-system/components/Filter/FilterDropdownButton.tsx';
import { MoreFiltersButton } from '@openx/design-system/components/Filter/MoreFiltersButton.tsx';
import { Tab } from '@openx/design-system/components/Tabs/Tab.tsx';
import { Tabs } from '@openx/design-system/components/Tabs/Tabs.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { UserRole } from '@openx/types/user.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { isAfter, isBefore, startOfDay } from 'date-fns';
import { type SyntheticEvent, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDateRangeChipOptions } from '../../../DealInsights/hooks/useDateRangeChipOptions.ts';
import { getDealInsightsSupportedDateRange } from '../../../DealInsights/utils/getDealInsightsSupportedDateRange.ts';
import { DEAL_INSIGHTS_V2_PATH, DEAL_INSIGHTS_V2_TABS } from '../../constants.ts';
import { useDealInsightsV2Context } from '../../context/DealInsightsV2Context.tsx';

export const DealInsightsV2Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dateRange, setDateRange } = useDealInsightsV2Context();
  const { useIsAllowed } = usePermissionContext();
  const { accessUser, isAllowed } = useIsAllowed();
  // same definition of an internal user as usePackagePermissions
  const isInternalUser =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
    !!accessUser?.roles.includes(UserRole.NETWORK_OA_INTERNAL);
  const { minDate, maxDate } = getDealInsightsSupportedDateRange();
  const dateRangeChipOptions = useDateRangeChipOptions(maxDate);

  // The existing Deal Insights shortcuts, not the design-system defaults. Presets reaching outside the supported
  // range (e.g. "Last quarter" on the last day of a quarter) are dropped; the bounds compare by day.
  const presets: FilterDatePreset[] = dateRangeChipOptions
    .filter(
      ({ startDate, endDate }) => !isBefore(startOfDay(startDate), minDate) && !isAfter(startOfDay(endDate), maxDate)
    )
    .map(({ label, startDate, endDate }) => ({
      label,
      getValue: () => [startDate, endDate],
    }));

  const lastSegment = location.pathname.split('/').pop() ?? '';
  const tabIndex = Math.max(
    DEAL_INSIGHTS_V2_TABS.findIndex(tab => tab.path === lastSegment),
    0
  );

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, index: number) => {
      const { path } = DEAL_INSIGHTS_V2_TABS[index];
      navigate(path ? `${DEAL_INSIGHTS_V2_PATH}/${path}` : DEAL_INSIGHTS_V2_PATH);
    },
    [navigate]
  );

  // fires on committed values only, never on a half-picked range
  const handleDateRangeChange = useCallback(
    (_event: SyntheticEvent | null, value: FilterDateValue) => {
      if (Array.isArray(value) && value[0]) {
        setDateRange({ startDate: value[0], endDate: value[1] });
      }
    },
    [setDateRange]
  );

  return (
    <Box
      data-test="deal-insights-v2-header"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        // margin, not padding: the line under the tabs is inset to the page padding in the design
        mx: 3,
        backgroundColor: 'background.default',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        appearance="line"
        value={tabIndex}
        onChange={handleTabChange}
        aria-label="Deal Insights"
        // The DS Tabs draw their own baseline only under the tabs; the design has one line across the whole row,
        // which is the `borderBottom` of this header, so the Tabs' own one would show up as a second, shorter line.
        sx={{ '&:not(.MuiTabs-vertical)': { borderBottom: 'none' } }}
      >
        {DEAL_INSIGHTS_V2_TABS.map((tab, index) => (
          <Tab key={tab.path} value={index} label={tab.name} />
        ))}
      </Tabs>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <FilterDateButton
          data-test="deal-insights-v2-date-range-picker"
          mode="range"
          label="Date"
          // a null end means "up to the latest supported date" (see the context)
          value={[dateRange.startDate, dateRange.endDate ?? maxDate]}
          presets={presets}
          minDate={minDate}
          maxDate={maxDate}
          clearable={false}
          rangeSeparator="-"
          onChange={handleDateRangeChange}
        />
        {/* TODO(EXCH-19446): options and wiring */}
        <FilterDropdownButton data-test="deal-insights-v2-deal-filter" panel label="Deal" options={[]} />
        {isInternalUser && (
          <FilterDropdownButton
            data-test="deal-insights-v2-organization-filter"
            panel
            label="Organization"
            options={[]}
          />
        )}
        {/* TODO(EXCH-19446): drawer fields and filter wiring */}
        <MoreFiltersButton data-test="deal-insights-v2-more-filters" drawerTitle="More Filters" />
      </Box>
    </Box>
  );
};
